import { FC } from 'react'
import Swal from 'sweetalert2'
import { UsuarioModal } from '../../components/modal/UsuarioModal'
import {
  deleteUsuario,
  desbloquearUsuario,
  getUserById,
  resetar2faUsuario,
  toggleStatusUsuario,
} from '../../core/_requests'
import { UsuarioList } from '../../core/_models'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import {
  IconPaths,
  ModalSize,
  showAlertLoading,
  showConfirmAlert,
  showError,
} from '_metronic/helpers'
import { RowActions, useModal, useToast } from 'app/components'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  usuario: UsuarioList
}

export const UsuariosActionsCell: FC<Props> = ({ usuario }) => {
  const { showModal } = useModal()
  const { showToast } = useToast()
  const { refetch } = useQueryResponse()
  const { t } = useT()
  const nome = usuario.nomeSocial || usuario.nome || usuario.email || 'Usuário'

  const editUsuario = () =>
    showModal({
      title: t('modal.editar_usuario'),
      content: <UsuarioModal />,
      size: ModalSize.ExtraLarge,
      fetchers: {
        usuario: () => getUserById(usuario.id),
      },
    })

  const setStatus = async (ativo: boolean) => {
    try {
      showAlertLoading()
      await toggleStatusUsuario(usuario.id, ativo)
      Swal.close()
      showToast({ variant: 'success', title: `${nome} foi ${ativo ? 'ativado' : 'inativado'}.` })
      refetch()
    } catch (error) {
      showError(error)
    }
  }

  // Inativar pede confirmação (corta o acesso); ativar é reversível e não pede.
  const askInativar = async () => {
    const confirmed = await showConfirmAlert({
      title: 'Inativar usuário?',
      text: `${nome} não conseguirá mais acessar o sistema. Você pode reativá-lo quando quiser.`,
      confirmButtonText: 'Inativar',
      cancelButtonText: 'Cancelar',
      danger: true,
    })
    if (confirmed) await setStatus(false)
  }

  const resetar2fa = async () => {
    try {
      const isConfirmed = await showConfirmAlert({
        title: 'Resetar a verificação em duas etapas?',
        text: `O autenticador e os códigos de recuperação de ${
          usuario.nome ?? usuario.email ?? 'este usuário'
        } serão removidos. Se a instituição exigir a verificação, ele refaz o cadastro no próximo login. Confirme a identidade de quem pediu antes de continuar.`,
        icon: 'warning',
        confirmButtonText: 'Sim, resetar',
      })
      if (!isConfirmed) return

      showAlertLoading()
      await resetar2faUsuario(usuario.id)
      Swal.close()
      showToast({
        variant: 'success',
        title: 'Verificação em duas etapas removida.',
        description: 'O usuário fará um novo cadastro ao entrar.',
      })
    } catch (error) {
      showError(error)
    }
  }

  const desbloquear = async () => {
    try {
      const isConfirmed = await showConfirmAlert({
        title: 'Desbloquear o acesso?',
        text: `${nome} poderá tentar entrar de novo, com as tentativas zeradas. Confirme a identidade de quem pediu antes de continuar.`,
        icon: 'warning',
        confirmButtonText: 'Sim, desbloquear',
      })
      if (!isConfirmed) return

      showAlertLoading()
      await desbloquearUsuario(usuario.id)
      Swal.close()
      showToast({ variant: 'success', title: `Acesso de ${nome} desbloqueado.` })
    } catch (error) {
      showError(error)
    }
  }

  const askDelete = async () => {
    const confirmed = await showConfirmAlert({
      title: 'Excluir usuário?',
      text: `${nome} será excluído. Para só bloquear o acesso, prefira inativar.`,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      danger: true,
    })
    if (!confirmed) return

    try {
      showAlertLoading()
      await deleteUsuario(usuario.id)
      Swal.close()
      showToast({ variant: 'success', title: `${nome} foi excluído.` })
      refetch()
    } catch (error) {
      showError(error)
    }
  }

  return (
    <RowActions
      itemName={nome}
      primaryAction={{ label: t('common.edit'), onClick: editUsuario, icon: IconPaths.Pen }}
      items={[
        { label: 'Desbloquear acesso', onClick: desbloquear },
        { label: 'Resetar verificação em duas etapas', onClick: resetar2fa },
        usuario.ativo
          ? { label: 'Inativar', onClick: askInativar, divider: true }
          : { label: 'Ativar', onClick: () => setStatus(true), divider: true },
        { label: t('common.delete'), onClick: askDelete, danger: true },
      ]}
    />
  )
}
